'use client'

import type { ComponentProps, ReactNode } from 'react'

import { InfoTip } from '@/components/ui/info-tip'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'

/*
 * The building blocks every order-type parameter section (TWAP, Advanced TWAP, RFQ, Delta
 * Hedge, Spreader) composes, so switching tabs never changes what a label, field or toggle
 * looks like:
 *
 * - FormSection   — a labelled block: label (+ optional info tip), the control(s), then one
 *                   hint or error line.
 * - FormToggleRow — an on/off option: label left, Switch right. Anything it enables renders
 *                   directly beneath it, unboxed (no nested bordered panels inside a widget).
 * - FormInput     — the field surface, optionally with a muted in-field label on the left
 *                   (so the name stays visible after typing, unlike a placeholder).
 * - FormFlagChip  — a compact order flag (Reduce, Post) toggled in place. Order flags use
 *                   this in every tab; FormToggleRow is for the tab's own settings.
 * - Modes (2–4 fixed choices) use SegmentedControl `fill`, never a dropdown or a hand-rolled
 *   button row.
 *
 * Motion: none beyond SegmentedControl's own sliding chip. These forms are used many times a
 * day, so revealed fields appear instantly rather than animating in.
 */

// Same surface as TwapUnitInput / LimitPriceField: hairline box, faint fill, 36px.
export const ORDER_FORM_FIELD_CLASS =
  'border-foreground/10 bg-foreground/5 text-foreground hover:border-foreground/30 focus-within:border-foreground/40 data-[state=open]:border-foreground/40 h-9 border px-3 text-sm transition-colors outline-none'

export const ORDER_FORM_LABEL_CLASS = 'text-foreground/60 text-sm tracking-tight'

function LabelRow({ label, info }: { label: ReactNode; info?: string }) {
  return (
    <span className="flex items-center gap-1">
      <span className={ORDER_FORM_LABEL_CLASS}>{label}</span>
      {info && <InfoTip text={info} />}
    </span>
  )
}

export function FormMessage({ error, hint }: { error?: ReactNode; hint?: ReactNode }) {
  if (error) return <p className="text-destructive text-xs">{error}</p>
  if (hint) return <p className="text-foreground/40 text-xs">{hint}</p>
  return null
}

interface FormSectionProps {
  label?: ReactNode
  info?: string
  hint?: ReactNode
  error?: ReactNode
  children: ReactNode
  className?: string
  'data-tutorial'?: string
}

export function FormSection({
  label,
  info,
  hint,
  error,
  children,
  className,
  'data-tutorial': dataTutorial,
}: FormSectionProps) {
  return (
    <div data-tutorial={dataTutorial} className={cn('flex flex-col gap-2', className)}>
      {label != null && <LabelRow label={label} info={info} />}
      {children}
      <FormMessage error={error} hint={hint} />
    </div>
  )
}

interface FormToggleRowProps {
  label: ReactNode
  info?: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  disabled?: boolean
  /** Fields this option enables — shown beneath the row only while it's on. */
  children?: ReactNode
  'data-tutorial'?: string
}

export function FormToggleRow({
  label,
  info,
  checked,
  onCheckedChange,
  disabled,
  children,
  'data-tutorial': dataTutorial,
}: FormToggleRowProps) {
  return (
    <div data-tutorial={dataTutorial} className="flex flex-col gap-2">
      <label
        className={cn(
          'flex min-h-6 items-center justify-between gap-3',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        )}
      >
        <LabelRow label={label} info={info} />
        <Switch checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
      </label>
      {checked && children}
    </div>
  )
}

interface FormInputProps extends Omit<ComponentProps<'input'>, 'type'> {
  /** Muted name pinned inside the left edge; the value stays right-aligned. */
  label?: ReactNode
  /** Muted unit pinned inside the right edge (`%`, `ms`, ...). */
  unit?: ReactNode
  containerClassName?: string
}

export function FormInput({
  label,
  unit,
  className,
  containerClassName,
  ...props
}: FormInputProps) {
  return (
    <div className={cn(ORDER_FORM_FIELD_CLASS, 'flex items-center gap-2', containerClassName)}>
      {label != null && (
        <span className="text-foreground/40 shrink-0 text-sm whitespace-nowrap">{label}</span>
      )}
      <input
        type="text"
        autoComplete="off"
        data-1p-ignore
        className={cn(
          'placeholder:text-foreground/30 h-full min-w-0 flex-1 bg-transparent text-right outline-none',
          className
        )}
        {...props}
      />
      {unit != null && <span className="text-foreground/40 shrink-0 text-sm">{unit}</span>}
    </div>
  )
}

interface FormFlagChipProps {
  pressed: boolean
  onPressedChange: (pressed: boolean) => void
  disabled?: boolean
  title?: string
  children: ReactNode
}

// On = filled emphasis chip with a visible border and medium weight; off = transparent, faint
// border and dimmed text, so the two states read apart at a glance. The medium weight is
// wider than regular, so an invisible medium-weight copy of the label shares the same grid
// cell and holds the chip at its pressed width — toggling never nudges its neighbours.
export function FormFlagChip({
  pressed,
  onPressedChange,
  disabled,
  title,
  children,
}: FormFlagChipProps) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={() => onPressedChange(!pressed)}
      disabled={disabled}
      title={title}
      className={cn(
        'grid h-8 cursor-pointer place-items-center border px-2.5 text-sm tracking-tight transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
        pressed
          ? 'bg-emphasis text-emphasis-foreground border-foreground/40'
          : 'text-foreground/40 border-foreground/15 hover:bg-foreground/5 hover:text-foreground/80 bg-transparent'
      )}
    >
      <span className={cn('col-start-1 row-start-1', pressed && 'font-medium')}>{children}</span>
      <span aria-hidden="true" className="invisible col-start-1 row-start-1 font-medium">
        {children}
      </span>
    </button>
  )
}
